import {
  Clock3,
  Clock6,
  Clock9,
  Clock12,
  Castle,
  Briefcase,
} from "lucide-react";
import { cn } from "@/utils/stylesUtils";

const TIMER_OPTIONS = [
  {
    label: "10 seconds",
    value: "10 seconds",
    Icon: Clock3,
  },
  { label: "20 seconds", value: "20 seconds", Icon: Clock6 },
  { label: "30 seconds", value: "30 seconds", Icon: Clock9 },
  { label: "1 minute", value: "1 minute", Icon: Clock12 },
  { label: "2 minutes", value: "2 minutes", Icon: Clock12, isDouble: true },
];

const VOICE_OPTIONS = [
  { label: "Lina (English)", value: "english female" },
  { label: "Drew (English)", value: "english male" },
  { label: "Lina (Spanish)", value: "spanish female" },
];

function SectionCard({ children, mobile }) {
  return (
    <div
      className={cn(
        "flex flex-col gap-2",
        mobile ? "p-3" : "p-3 border border-[#DDDDDD] rounded",
      )}
    >
      {children}
    </div>
  );
}

function OptionButton({ selected, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-1 px-2.5 py-1.5 rounded transition text-left",
        selected
          ? "bg-[#3386D912] border border-[#3286DA]"
          : "bg-white border border-[#DDDDDD] hover:bg-gray-100",
      )}
    >
      {children}
    </button>
  );
}

function CardOption({ selected, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex flex-col p-4 rounded text-left",
        selected
          ? "bg-[#3386D912] border border-[#3286DA]"
          : "bg-white border border-[#DDDDDD]",
      )}
    >
      {children}
    </button>
  );
}

export function SystemSection({
  mobile = false,
  wellnessTimer,
  voice,
  adultActionImages,
  usesCastle,
  showTourButton = false,
  onboardingUserKey = "unknown",
  handleSettingChange,
  setWellnessTimer,
  setVoice,
  setAdultActionImages,
  setUsesCastle,
}) {
  const listClass = `flex ${mobile ? "flex-col" : "flex-wrap"} gap-2.5 pb-1`;
  const onboardingTourStorageKey = `onboarding_tour_v1:${onboardingUserKey}`;
  const onboardingBeaconsStorageKey = `onboarding_beacons_v1:${onboardingUserKey}`;

  return (
    <div className={cn(!mobile && "flex flex-col gap-2.5 w-full")}>
      <SectionCard mobile={mobile}>
        <div className="text-[16px] font-bold">Wellness Timer</div>
        <div className="text-[16px] opacity-50">
          Choose how long you'd like to practice wellness during step 3
        </div>
        <div className={listClass}>
          {TIMER_OPTIONS.map(({ label, value, Icon, isDouble }) => (
            <OptionButton
              key={value}
              selected={wellnessTimer === value}
              onClick={() =>
                handleSettingChange("wellnesstimer", value, setWellnessTimer)
              }
            >
              <div className="flex items-center gap-1">
                {isDouble ? (
                  <>
                    <Icon size={16} />
                    <Icon size={16} />
                  </>
                ) : (
                  <Icon size={16} />
                )}
              </div>
              <span>{label}</span>
            </OptionButton>
          ))}
        </div>
      </SectionCard>
      <SectionCard mobile={mobile}>
        <div className="text-[16px] font-bold">Voice Guide</div>
        <div className="text-[16px] opacity-50">
          Select the voice that will guide you through your wellness practice
        </div>
        <div className={listClass}>
          {VOICE_OPTIONS.map(({ label, value }) => (
            <OptionButton
              key={value}
              selected={voice === value}
              onClick={() => handleSettingChange("voice", value, setVoice)}
            >
              <span>{label}</span>
            </OptionButton>
          ))}
        </div>
      </SectionCard>
      <SectionCard mobile={mobile}>
        <div className="text-[16px] font-bold">Illustration Style</div>
        <div className="text-[16px] opacity-50">
          Choose age-appropriate imagery for your wellness practice
        </div>
        <div className="flex flex-col gap-3">
          <CardOption
            selected={adultActionImages}
            onClick={() =>
              handleSettingChange(
                "adultActionImages",
                true,
                setAdultActionImages,
              )
            }
          >
            <span className="font-semibold flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Adult Illustration
            </span>
            <span className="ml-7">
              Illustrations of adults in the wellness practice
            </span>
          </CardOption>
          <CardOption
            selected={!adultActionImages}
            onClick={() =>
              handleSettingChange(
                "adultActionImages",
                false,
                setAdultActionImages,
              )
            }
          >
            <span className="font-semibold flex items-center gap-2">
              <Castle className="w-auto h-5" />
              Children's Illustrations
            </span>
            <span className="ml-8">
              Illustrations of children in the wellness practice
            </span>
          </CardOption>
        </div>
      </SectionCard>
      <SectionCard mobile={mobile}>
        <div className="text-[16px] font-bold">Interface Theme</div>
        <div className="text-[16px] opacity-50">
          Choose age-appropriate design for your interface
        </div>
        <div className="flex flex-col gap-3">
          <CardOption
            selected={!usesCastle}
            onClick={() =>
              handleSettingChange("usesCastle", false, setUsesCastle)
            }
          >
            <span className="font-semibold flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Adult Theme
            </span>
            <span className="ml-7">Clean, professional interface design</span>
          </CardOption>
          <CardOption
            selected={usesCastle}
            onClick={() =>
              handleSettingChange("usesCastle", true, setUsesCastle)
            }
          >
            <span className="font-semibold flex items-center gap-2">
              <Castle className="w-auto h-5" />
              Children's Theme (Castle)
            </span>
            <span className="ml-8">
              Colorful, playful interface with castle elements
            </span>
          </CardOption>
        </div>
      </SectionCard>
      {showTourButton && (
        <SectionCard mobile={mobile}>
          <div className="text-[16px] font-bold">App Tour</div>
          <div className="text-[16px] opacity-50">
            Take a tour of the app anytime
          </div>
          <div>
            <button
              type="button"
              className="px-3 py-2 text-base rounded bg-[#3386D912] hover:bg-[#3386D924] border border-[#3286DA]"
              data-onboarding-id="settings-retake-tour"
              onClick={() => {
                try {
                  localStorage.removeItem(onboardingTourStorageKey);
                  localStorage.removeItem(onboardingBeaconsStorageKey);
                } catch {
                  // ignore storage errors
                }
                window.dispatchEvent(
                  new CustomEvent("onboarding:retake", {
                    detail: { stepId: "sidebar-menu" },
                  }),
                );
              }}
            >
              Take Tour
            </button>
          </div>
        </SectionCard>
      )}
    </div>
  );
}
